import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles, FileText, Download, Trash2, Edit, RefreshCw, Copy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookDetail = ({ user }) => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [outline, setOutline] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteChapterConfirm, setShowDeleteChapterConfirm] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState(null);
  const [generateMode, setGenerateMode] = useState("outline");
  const [generationProgress, setGenerationProgress] = useState({ percentage: 0, chaptersCompleted: 0, totalChapters: 0 });
  const [autoContinue, setAutoContinue] = useState(false);
  const autoContinueRef = useRef(false);  // Persist across re-renders
  const bookStatusRef = useRef(null);  // Track book status for polling
  const [batchInfo, setBatchInfo] = useState({ current: 0, total: 0 });

  // Update ref whenever book changes
  useEffect(() => {
    if (book) {
      bookStatusRef.current = book.status;
    }
  }, [book]);

  useEffect(() => {
    loadBookData();
    const interval = setInterval(async () => {
      const currentStatus = bookStatusRef.current;
      if (currentStatus && (currentStatus === "writing" || currentStatus === "outline" || currentStatus === "generating_batch" || currentStatus === "partial")) {
        // Poll for updates
        try {
          const bookRes = await axios.get(`${API}/books/${bookId}`);
          const chaptersRes = await axios.get(`${API}/books/${bookId}/chapters`);
          
          const newBook = bookRes.data;
          const newChapters = chaptersRes.data;
          
          setBook(newBook);
          setChapters(newChapters);
          bookStatusRef.current = newBook.status;
          
          // Update progress
          const completedChapters = newChapters.filter(ch => ch.status === "completed").length;
          const totalChapters = newBook.total_chapters || newChapters.length || 15;
          const percentage = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;
          
          setGenerationProgress({
            percentage,
            chaptersCompleted: completedChapters,
            totalChapters
          });
          
          // Check if generation is complete
          if (newBook.status === "completed") {
            console.log("Book generation completed! Status:", newBook.status);
            setGenerating(false);
            setAutoContinue(false);
            autoContinueRef.current = false;
            toast.success("🎉 Book generation completed! All chapters done.", { duration: 5000 });
          } else if (completedChapters >= totalChapters && newBook.status !== "completed") {
            // All chapters done but status not updated - force refresh
            console.log("All chapters completed, forcing status check...");
            await axios.get(`${API}/books/${bookId}`).then(res => {
              setBook(res.data);
              bookStatusRef.current = res.data.status;
              if (res.data.status === "completed") {
                setGenerating(false);
                setAutoContinue(false);
                autoContinueRef.current = false;
                toast.success("🎉 Book generation completed!", { duration: 5000 });
              }
            });
          }
        } catch (error) {
          console.log("Polling error (ignored):", error.message);
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [bookId]);

  const loadBookData = async (retryCount = 0) => {
    try {
      const bookRes = await axios.get(`${API}/books/${bookId}`);
      setBook(bookRes.data);

      if (bookRes.data.status !== "draft") {
        try {
          const outlineRes = await axios.get(`${API}/books/${bookId}/outline`);
          setOutline(outlineRes.data);
        } catch (error) {
          console.log("Outline not available yet");
        }

        try {
          const chaptersRes = await axios.get(`${API}/books/${bookId}/chapters`);
          const loadedChapters = chaptersRes.data;
          setChapters(loadedChapters);
          console.log(`Loaded ${loadedChapters.length} chapters`);
          
          // Initialize progress display - use backend's chapters_completed for accuracy
          if (bookRes.data.total_chapters) {
            const completedChapters = bookRes.data.chapters_completed || loadedChapters.filter(ch => ch.status === "completed").length;
            const percentage = Math.round((completedChapters / bookRes.data.total_chapters) * 100);
            setGenerationProgress({
              percentage: percentage,
              chaptersCompleted: completedChapters,
              totalChapters: bookRes.data.total_chapters
            });
          }
        } catch (error) {
          console.error("Error loading chapters:", error);
          toast.error("Failed to load chapters");
        }
      }
      // Success - stop loading
      setLoading(false);
    } catch (error) {
      console.error("Failed to load book:", error);
      // Auto-retry up to 2 times with increasing delay
      if (retryCount < 2) {
        console.log(`[BookDetail] Retrying loadBookData (attempt ${retryCount + 2}/3)...`);
        setTimeout(() => loadBookData(retryCount + 1), 1000 * (retryCount + 1));
        return; // Don't show error or navigate yet
      }
      toast.error("Failed to load book. Returning to dashboard.");
      setLoading(false);
      navigate("/dashboard");
    }
  };

  const loadChapters = async () => {
    try {
      const chaptersRes = await axios.get(`${API}/books/${bookId}/chapters`);
      setChapters(chaptersRes.data);
    } catch (error) {
      // Ignore
    }
  };

  const handleContinueBatch = async (isAutoContinue = false) => {
    try {
      setGenerating(true);
      if (!isAutoContinue) {
        toast.info("Starting batch generation...");
      }
      
      const response = await axios.post(`${API}/books/${bookId}/continue-batch`);
      
      if (response.data.success) {
        const chapterList = response.data.chapters_to_generate?.join(', ') || `${response.data.batch_start}-${response.data.batch_end}`;
        const batchesRemaining = response.data.batches_remaining || 1;
        const totalBatches = Math.ceil(response.data.total_chapters / 3);
        const currentBatch = totalBatches - batchesRemaining + 1;
        
        setBatchInfo({ current: currentBatch, total: totalBatches });
        
        if (isAutoContinue) {
          toast.info(`Auto-continuing: Batch ${currentBatch}/${totalBatches} (Chapters ${chapterList})`);
        } else {
          toast.success(`Generating chapters: ${chapterList}`);
        }
        
        // Start polling for progress
        const progressInterval = setInterval(async () => {
          try {
            const bookRes = await axios.get(`${API}/books/${bookId}`);
            const chaptersRes = await axios.get(`${API}/books/${bookId}/chapters`);
            
            setBook(bookRes.data);
            setChapters(chaptersRes.data);
            
            const completedChapters = chaptersRes.data.filter(ch => ch.status === "completed").length;
            const totalChapters = bookRes.data.total_chapters || 15;
            const percentage = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;
            
            setGenerationProgress({
              percentage,
              chaptersCompleted: completedChapters,
              totalChapters
            });
            
            if (bookRes.data.status === "completed" || completedChapters === totalChapters) {
              clearInterval(progressInterval);
              setGenerating(false);
              setAutoContinue(false);
              autoContinueRef.current = false;  // Clear ref too
              toast.success("🎉 Book generation completed! All chapters done.");
            } else if (bookRes.data.status === "partial") {
              // Don't clear interval yet - wait for auto-continue decision
              const isAutoMode = autoContinueRef.current;  // Use ref for persistent state
              
              if (isAutoMode) {
                // Auto-continue mode: Start next batch automatically
                console.log("Auto-continue: Batch completed, starting next batch...");
                toast.info(`Batch completed! ${completedChapters}/${totalChapters} chapters done. Starting next batch in 3 seconds...`);
                
                // Clear current interval
                clearInterval(progressInterval);
                
                // Start next batch after short delay
                setTimeout(() => {
                  console.log("Auto-continue: Triggering next batch");
                  handleContinueBatch(true);
                }, 3000);
              } else {
                // Manual mode: Stop and wait for user
                clearInterval(progressInterval);
                setGenerating(false);
                toast.success(`Batch completed! ${completedChapters}/${totalChapters} chapters done. Click to continue.`);
              }
            }
          } catch (error) {
            // Ignore polling errors
          }
        }, 3000);
      } else {
        toast.error(response.data.message || "Failed to continue generation");
        setGenerating(false);
      }
    } catch (error) {
      toast.error("Failed to continue generation");
      setGenerating(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setShowGenerate(false);
    setGenerationProgress({ percentage: 0, chaptersCompleted: 0, totalChapters: 0 });

    try {
      // Start generation
      await axios.post(`${API}/books/${bookId}/generate`, { mode: generateMode });
      
      if (generateMode === "outline") {
        toast.success("Outline generated!");
        setTimeout(() => loadBookData(), 2000);
      } else {
        toast.success("Book generation started!");
        
        // Poll for progress
        const pollInterval = setInterval(async () => {
          try {
            const response = await axios.get(`${API}/books/${bookId}`);
            const bookData = response.data;
            
            // Get chapters to calculate progress
            const chaptersResponse = await axios.get(`${API}/books/${bookId}/chapters`);
            const allChapters = chaptersResponse.data;
            const completedChapters = allChapters.filter(ch => ch.status === "completed").length;
            const totalChapters = allChapters.length;
            const percentage = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;
            
            setGenerationProgress({
              percentage,
              chaptersCompleted: completedChapters,
              totalChapters
            });
            
            // Check if generation is complete
            if (bookData.status === "completed" || completedChapters === totalChapters) {
              clearInterval(pollInterval);
              setGenerating(false);
              toast.success("Book generation completed!");
              loadBookData();
            }
          } catch (error) {
            console.error("Error polling progress:", error);
          }
        }, 3000); // Poll every 3 seconds
        
        // Stop polling after 30 minutes (safety timeout)
        setTimeout(() => {
          clearInterval(pollInterval);
          if (generating) {
            setGenerating(false);
            toast.info("Generation is still in progress. Refresh the page to check status.");
          }
        }, 30 * 60 * 1000);
      }
    } catch (error) {
      toast.error("Failed to generate");
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    console.log("Delete confirmed for book:", bookId);
    setShowDeleteConfirm(false);

    try {
      const response = await axios.delete(`${API}/books/${bookId}`);
      console.log("Delete response:", response);
      toast.success("Book deleted successfully");
      navigate("/dashboard");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete book");
    }
  };

  const handleDeleteChapter = async () => {
    if (!chapterToDelete) return;
    
    try {
      await axios.delete(`${API}/chapters/${chapterToDelete.id}`);
      toast.success(`Chapter ${chapterToDelete.chapter_number} deleted successfully`);
      // Refresh chapters list
      setChapters(chapters.filter(ch => ch.id !== chapterToDelete.id));
    } catch (error) {
      console.error("Delete chapter error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete chapter");
    } finally {
      setShowDeleteChapterConfirm(false);
      setChapterToDelete(null);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      toast.info('Preparing your book content...');
      
      // Format all chapters into text
      let bookText = `${book.title}\n`;
      bookText += `${'='.repeat(book.title.length)}\n\n`;
      
      // Sort chapters by chapter number
      const sortedChapters = [...chapters].sort((a, b) => a.chapter_number - b.chapter_number);
      
      for (const chapter of sortedChapters) {
        bookText += `Chapter ${chapter.chapter_number}: ${chapter.title}\n\n`;
        bookText += `${chapter.content}\n\n`;
        bookText += `${'─'.repeat(50)}\n\n`;
      }
      
      console.log('[Copy] Attempting to copy:', {
        totalChapters: chapters.length,
        totalLength: bookText.length
      });
      
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(bookText);
          console.log('[Copy] Successfully copied via Clipboard API');
          toast.success(`Copied entire book (${chapters.length} chapters) to clipboard! Paste into Word.`);
          return;
        } catch (clipboardError) {
          console.warn('[Copy] Clipboard API failed, trying fallback:', clipboardError);
        }
      }
      
      // Fallback method using textarea (works in iframes)
      const textarea = document.createElement('textarea');
      textarea.value = bookText;
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.width = '2em';
      textarea.style.height = '2em';
      textarea.style.padding = '0';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      
      if (successful) {
        console.log('[Copy] Successfully copied via fallback method');
        toast.success(`Copied entire book (${chapters.length} chapters) to clipboard! Paste into Word.`);
      } else {
        throw new Error('Copy command failed');
      }
    } catch (error) {
      console.error("[Copy] All copy methods failed:", error);
      toast.error("Failed to copy. Please try selecting and copying the text manually from the 'Entire Book' tab.");
    }
  };

  const getProgress = () => {
    if (!chapters.length) return 0;
    const completed = chapters.filter((ch) => ch.status === "completed").length;
    return (completed / chapters.length) * 100;
  };

  // Detect missing or empty chapters (chapters that should exist but don't OR have low word count)
  const getMissingChapters = () => {
    if (!book) return [];
    const totalChapters = book.total_chapters || 10;
    
    // Get chapters that are completed with sufficient content (word_count > 500)
    const validChapterNumbers = new Set(
      chapters
        .filter(ch => ch.status === "completed" && (ch.word_count > 500 || (ch.content && ch.content.length > 500)))
        .map(ch => ch.chapter_number)
    );
    
    const missing = [];
    for (let i = 1; i <= totalChapters; i++) {
      if (!validChapterNumbers.has(i)) {
        missing.push(i);
      }
    }
    
    // Debug logging
    if (missing.length > 0) {
      console.log(`[Missing Chapters] Total expected: ${totalChapters}, Valid chapters: ${Array.from(validChapterNumbers).sort((a,b)=>a-b)}, Missing: ${missing}`);
    }
    return missing;
  };

  const missingChapters = getMissingChapters();
  
  // Also show button if chapters array has fewer items than total_chapters
  const hasMissingData = book && chapters.length < (book.total_chapters || 10);
  
  // Check if all chapters are actually complete but status is stuck
  const allChaptersDone = book && chapters.length >= (book.total_chapters || 10) && 
    chapters.filter(ch => ch.status === "completed").length >= (book.total_chapters || 10);
  const isStatusStuck = allChaptersDone && book.status !== "completed";

  // Force mark book as complete when status is stuck
  const handleMarkComplete = async () => {
    try {
      toast.loading("Updating book status...", { id: "mark-complete" });
      await axios.put(`${API}/books/${bookId}/status`, { status: "completed" });
      
      // Reload book data
      const bookRes = await axios.get(`${API}/books/${bookId}`);
      setBook(bookRes.data);
      bookStatusRef.current = bookRes.data.status;
      
      setGenerating(false);
      setAutoContinue(false);
      autoContinueRef.current = false;
      
      toast.success("🎉 Book marked as complete!", { id: "mark-complete", duration: 5000 });
    } catch (error) {
      console.error("Error marking book complete:", error);
      toast.error("Failed to update status. Please refresh the page.", { id: "mark-complete" });
    }
  };

  // Generate missing chapters
  const handleGenerateMissing = async () => {
    setGenerating(true);
    toast.loading(`Fixing stuck book and generating missing chapters...`, { id: "gen-missing" });
    
    try {
      // Check if user is authenticated
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please log in again to continue", { id: "gen-missing" });
        setGenerating(false);
        return;
      }
      
      // Use axios directly - interceptor will add auth header
      const response = await axios.post(`${API}/books/${bookId}/force-resume`);
      
      const missing = response.data.missing_chapters || [];
      if (missing.length > 0) {
        toast.success(`Generation started for chapters: ${missing.join(", ")}`, { id: "gen-missing" });
      } else {
        toast.success("Book is complete!", { id: "gen-missing" });
      }
      
      // Start polling for progress
      loadChapters();
      loadBookData();
    } catch (error) {
      console.error("Error generating missing chapters:", error);
      const errorMsg = error.response?.data?.detail || "Failed to start generation";
      
      // If token error, suggest re-login
      if (errorMsg.toLowerCase().includes("token") || error.response?.status === 401) {
        toast.error("Session expired. Please log out and log back in.", { id: "gen-missing" });
      } else {
        toast.error(errorMsg, { id: "gen-missing" });
      }
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-stone-100">
        <div className="spinner"></div>
      </div>
    );
  }

  // Safety check - if book failed to load, show error state
  if (!book) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-amber-50 to-stone-100 gap-4">
        <div className="text-lg text-stone-600">Failed to load book</div>
        <Button onClick={() => navigate("/dashboard")} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100">
      {/* Status Stuck Banner - All chapters done but status not updated */}
      {isStatusStuck && (
        <div className="bg-green-100 border-b border-green-300 px-4 py-3">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-800">
              <span className="font-semibold">✅ All chapters completed!</span>
              <span>
                Your book is ready. Click "Mark Complete" to finalize.
              </span>
            </div>
            <Button 
              onClick={handleMarkComplete}
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Mark Complete
            </Button>
          </div>
        </div>
      )}
      
      {/* Missing Chapters Alert Banner */}
      {book && !isStatusStuck && (missingChapters.length > 0 || hasMissingData || (book.status !== "completed" && chapters.length < (book.total_chapters || 15))) && (
        <div className="bg-amber-100 border-b border-amber-300 px-4 py-3">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800">
              <span className="font-semibold">⚠️ Book needs attention:</span>
              <span>
                {chapters.length} of {book.total_chapters || 15} chapters complete
                {missingChapters.length > 0 && ` (missing: ${missingChapters.join(", ")})`}
              </span>
            </div>
            <Button 
              onClick={handleGenerateMissing}
              disabled={generating}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${generating ? "animate-spin" : ""}`} />
              {generating ? "Working..." : "Fix & Resume"}
            </Button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="border-b border-stone-200 bg-white/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              data-testid="back-btn"
              variant="ghost" 
              onClick={() => navigate("/dashboard")}
              className="text-stone-600 hover:text-stone-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold playfair text-stone-900">{book.title}</h1>
              <p className="text-sm text-stone-600 capitalize">{book.genre}</p>
            </div>
            <Button 
              data-testid="delete-book-btn"
              variant="ghost" 
              onClick={() => setShowDeleteConfirm(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Status Card */}
        <Card className="mb-8 border-stone-200 shadow-lg" data-testid="status-card">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-xl playfair text-stone-900">Book Status</CardTitle>
                <p className="text-stone-600 mt-2">{book.description}</p>
              </div>
              {book.status === "draft" ? (
                <Button 
                  data-testid="generate-btn"
                  onClick={() => setShowGenerate(true)}
                  disabled={generating}
                  className="bg-amber-900 hover:bg-amber-800 text-white rounded-full"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {generating ? "Generating..." : "Generate Book"}
                </Button>
              ) : book.status === "partial" || book.status === "generating_batch" || book.status === "writing" ? (
                <Button 
                  data-testid="continue-batch-btn"
                  onClick={() => {
                    setAutoContinue(true);
                    autoContinueRef.current = true;  // Persist in ref
                    console.log("User clicked: Auto-continue mode ENABLED");
                    handleContinueBatch(false);
                  }}
                  disabled={generating || book.status === "generating_batch"}
                  className="bg-green-700 hover:bg-green-600 rounded-full"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${(generating || book.status === "generating_batch") ? "animate-spin" : ""}`} />
                  {generating || book.status === "generating_batch" ? "Generating in Progress..." : "Generate Remaining Chapters"}
                </Button>
              ) : book.status === "completed" && chapters.length > 0 ? (
                <div className="flex gap-2">
                  {(missingChapters.length > 0 || hasMissingData) && (
                    <Button 
                      onClick={handleGenerateMissing}
                      disabled={generating}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${generating ? "animate-spin" : ""}`} />
                      {missingChapters.length > 0 
                        ? `Generate Missing (Ch. ${missingChapters.join(", ")})`
                        : `Generate Missing Chapters`
                      }
                    </Button>
                  )}
                  <Button 
                    onClick={handleCopyToClipboard}
                    className="bg-green-700 hover:bg-green-600 text-white"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy to Clipboard
                  </Button>
                </div>
              ) : (missingChapters.length > 0 || hasMissingData || chapters.length < (book.total_chapters || 15)) ? (
                /* Fallback: Show resume button for ANY stuck state with missing chapters */
                <Button 
                  onClick={handleGenerateMissing}
                  disabled={generating}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${generating ? "animate-spin" : ""}`} />
                  {missingChapters.length > 0 
                    ? `Resume - Generate Missing (Ch. ${missingChapters.join(", ")})`
                    : `Resume Generation`
                  }
                </Button>
              ) : null}
            </div>
          </CardHeader>
          
          {/* Generation Progress Bar */}
          {generationProgress.totalChapters > 0 && (book.status === "partial" || book.status === "generating_batch" || book.status === "writing" || generating) && (
            <CardContent className="pt-0">
              <div className="space-y-3 bg-amber-50 border border-amber-200 rounded-lg p-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-stone-900">
                    {generating ? (
                      autoContinue && batchInfo.total > 0 
                        ? `Auto-Generating Book... (Batch ${batchInfo.current}/${batchInfo.total})`
                        : "Generating Book..."
                    ) : "Book Progress"}
                  </span>
                  <span className="text-sm font-bold text-amber-900">{generationProgress.percentage}%</span>
                </div>
                <Progress value={generationProgress.percentage} className="h-3" />
                <div className="flex justify-between items-center text-sm text-stone-600">
                  <span>Chapters Completed: {generationProgress.chaptersCompleted} / {generationProgress.totalChapters}</span>
                  <span className="text-amber-700 font-medium">
                    {(generating || book.status === "generating_batch") ? "In Progress..." : generationProgress.chaptersCompleted < generationProgress.totalChapters ? "Ready to Continue" : "Complete!"}
                  </span>
                </div>
                {autoContinue && generating && (
                  <div className="text-xs text-amber-800 mt-2 p-2 bg-amber-100 rounded border border-amber-300">
                    ✨ Auto-mode active: Batches will continue automatically. Keep this tab open!
                  </div>
                )}
              </div>
            </CardContent>
          )}
          
          {chapters.length > 0 && (
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-stone-600">
                  <span>Progress</span>
                  <span>{Math.round(getProgress())}%</span>
                </div>
                <Progress value={getProgress()} className="h-2" />
                <div className="text-sm text-stone-600">
                  {chapters.filter((ch) => ch.status === "completed").length} of {chapters.length} chapters completed
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Tabs */}
        {book.status !== "draft" && (
          <Tabs defaultValue="outline" className="space-y-6">
            <TabsList className="bg-white border border-stone-200">
              <TabsTrigger value="outline" data-testid="outline-tab">Outline</TabsTrigger>
              <TabsTrigger value="chapters" data-testid="chapters-tab">Chapters</TabsTrigger>
              <TabsTrigger value="entire-book" data-testid="entire-book-tab">Entire Book</TabsTrigger>
            </TabsList>

            <TabsContent value="outline" className="space-y-4">
              {outline ? (
                <>
                  <Card className="border-stone-200 shadow-md">
                    <CardHeader>
                      <CardTitle className="playfair text-stone-900">Overall Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-stone-700 leading-relaxed">{outline.overall_summary}</p>
                    </CardContent>
                  </Card>

                  <div className="space-y-3">
                    {outline.chapters.map((ch) => (
                      <Card key={ch.chapter_number} className="border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader>
                          <CardTitle className="text-lg playfair text-stone-900">
                            Chapter {ch.chapter_number}: {ch.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-stone-600">{ch.summary}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-stone-600">Loading outline...</div>
              )}
            </TabsContent>

            <TabsContent value="chapters" className="space-y-3">
              {chapters.length > 0 ? (
                <>
                  {book.status === "completed" && chapters.length > 0 && (
                    <div className="flex justify-end mb-4">
                      <Button 
                        onClick={handleCopyToClipboard}
                        size="sm"
                        className="bg-green-700 hover:bg-green-600 text-white"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy All Chapters
                      </Button>
                    </div>
                  )}
                  {chapters.map((chapter) => (
                    <Card 
                      key={chapter.id} 
                      className="border-stone-200 shadow-sm hover:shadow-md transition-all"
                      data-testid={`chapter-card-${chapter.chapter_number}`}
                    >
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => chapter.status === "completed" && navigate(`/chapter/${chapter.id}`)}
                          >
                            <CardTitle className="text-lg playfair text-stone-900">
                              Chapter {chapter.chapter_number}: {chapter.title}
                            </CardTitle>
                            {chapter.word_count > 0 && (
                              <p className="text-sm text-stone-600 mt-1">{chapter.word_count} words</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {chapter.status === "completed" ? (
                              <span className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                                Completed
                              </span>
                            ) : chapter.status === "generating" ? (
                              <span className="text-xs px-3 py-1 bg-amber-100 text-amber-700 rounded-full font-medium flex items-center gap-1">
                                <RefreshCw className="h-3 w-3 animate-spin" />
                                Generating
                              </span>
                            ) : (
                              <span className="text-xs px-3 py-1 bg-stone-200 text-stone-700 rounded-full font-medium">
                                Pending
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                setChapterToDelete(chapter);
                                setShowDeleteChapterConfirm(true);
                              }}
                              data-testid={`delete-chapter-${chapter.chapter_number}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </>
              ) : (
                <div className="text-center py-12 text-stone-600">No chapters yet</div>
              )}
            </TabsContent>

            <TabsContent value="entire-book" className="space-y-6">
              {chapters.length > 0 ? (
                <Card className="border-stone-200 shadow-md">
                  <CardHeader className="bg-gradient-to-r from-amber-50 to-stone-50 border-b border-stone-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="text-2xl playfair text-stone-900">{book.title}</CardTitle>
                        <p className="text-sm text-stone-600 mt-2">
                          {chapters.length} {chapters.length === 1 ? 'Chapter' : 'Chapters'} • {chapters.reduce((sum, ch) => sum + (ch.word_count || 0), 0).toLocaleString()} Total Words
                        </p>
                      </div>
                      {book.status === "completed" && chapters.length > 0 && (
                        <Button 
                          onClick={handleCopyToClipboard}
                          size="sm"
                          className="bg-green-700 hover:bg-green-600 text-white"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy All to Clipboard
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-8">
                    <div className="prose prose-stone max-w-none">
                      {chapters.map((chapter, index) => (
                        <div key={chapter.id} className="mb-12">
                          {/* Chapter Header */}
                          <div className="mb-6 pb-4 border-b-2 border-stone-300">
                            <h2 className="text-2xl font-bold playfair text-stone-900 mb-2">
                              Chapter {chapter.chapter_number}
                            </h2>
                            <h3 className="text-xl playfair text-stone-700 italic">
                              {chapter.title}
                            </h3>
                            {chapter.word_count > 0 && (
                              <p className="text-sm text-stone-500 mt-2">
                                {chapter.word_count} words
                              </p>
                            )}
                          </div>
                          
                          {/* Chapter Content */}
                          {chapter.content ? (
                            <div className="text-base leading-relaxed text-stone-800 whitespace-pre-wrap font-serif">
                              {chapter.content}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-stone-500 italic">
                              Chapter content not available
                            </div>
                          )}
                          
                          {/* Chapter Divider */}
                          {index < chapters.length - 1 && (
                            <div className="flex items-center justify-center my-12">
                              <div className="flex-1 border-t border-stone-300"></div>
                              <span className="px-4 text-stone-400">❧</span>
                              <div className="flex-1 border-t border-stone-300"></div>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* End of Book */}
                      <div className="text-center mt-16 pt-8 border-t-2 border-stone-300">
                        <p className="text-lg playfair text-stone-600 italic">— The End —</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="text-center py-12 text-stone-600">No chapters to display</div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Book?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;<strong>{book?.title}</strong>&rdquo;? This will permanently remove the book, its outline, and all chapters. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              data-testid="delete-confirm-btn"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Book
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Chapter Confirmation Dialog */}
      <AlertDialog open={showDeleteChapterConfirm} onOpenChange={setShowDeleteChapterConfirm}>
        <AlertDialogContent data-testid="delete-chapter-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Chapter?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>Chapter {chapterToDelete?.chapter_number}: {chapterToDelete?.title}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setChapterToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteChapter}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Chapter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Generate Dialog */}
      <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
        <DialogContent data-testid="generate-dialog" aria-describedby="generate-dialog-description">
          <DialogHeader>
            <DialogTitle className="text-2xl playfair text-stone-900">Generate Book</DialogTitle>
            <p id="generate-dialog-description" className="sr-only">Choose how to generate your book with AI</p>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-stone-600">
              Choose how you want to generate your book:
            </p>

            <RadioGroup value={generateMode} onValueChange={setGenerateMode}>
              <div className="flex items-start space-x-3 p-4 border border-stone-200 rounded-lg hover:border-amber-900 transition-colors cursor-pointer">
                <RadioGroupItem value="outline" id="outline-mode" data-testid="mode-outline" />
                <div className="flex-1">
                  <Label htmlFor="outline-mode" className="font-semibold cursor-pointer text-stone-900">
                    Outline Only
                  </Label>
                  <p className="text-sm text-stone-600 mt-1">
                    Generate a detailed chapter outline. You can review and then generate chapters individually or all at once.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 border border-stone-200 rounded-lg hover:border-amber-900 transition-colors cursor-pointer">
                <RadioGroupItem value="full" id="full-mode" data-testid="mode-full" />
                <div className="flex-1">
                  <Label htmlFor="full-mode" className="font-semibold cursor-pointer text-stone-900">
                    Complete Book
                  </Label>
                  <p className="text-sm text-stone-600 mt-1">
                    Generate the outline and all chapters automatically. This may take 10-20 minutes depending on book length.
                  </p>
                </div>
              </div>
            </RadioGroup>

            <div className="flex gap-3 pt-4">
              <Button 
                data-testid="cancel-generate-btn"
                variant="outline" 
                onClick={() => setShowGenerate(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                data-testid="confirm-generate-btn"
                onClick={handleGenerate}
                disabled={generating}
                className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
              >
                {generating ? "Generating..." : "Start Generation"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookDetail;